/* ===========================================================
   editor/convert.js — between the doc's forms

   Yjs doc  ⇄  ProseMirror JSON  (history, compare, merge, search)
   applyJSON() changes a live Yjs doc to match some JSON with the
   smallest edit it can find (y-prosemirror's updateYFragment), so
   restoring a version or merging a copy shows up for collaborators as
   ordinary edits, and their cursors stay put where nothing changed.
   =========================================================== */

import * as Y from 'yjs';
import { yXmlFragmentToProsemirrorJSON, updateYFragment, prosemirrorJSONToYDoc } from '@tiptap/y-tiptap';
import { docSchema } from './extensions.js';
import { emptyDoc, textOf, headingsOf, snippetOf, wordCount, tidy } from '../../shared/blocks.js';

export const FIELD = 'default';

/** Normalise any doc JSON through the schema (fills default attrs, drops junk). */
export function normalize(json) {
  try { return docSchema().nodeFromJSON(tidy(json || emptyDoc())).toJSON(); } catch { return emptyDoc(); }
}

export function toJSON(ydoc, field = FIELD) {
  const frag = ydoc.getXmlFragment(field);
  if (!frag.length) return emptyDoc();
  return normalize(yXmlFragmentToProsemirrorJSON(frag));
}

export function applyJSON(ydoc, json, origin = 'dp:apply', field = FIELD) {
  const node = docSchema().nodeFromJSON(normalize(json));
  ydoc.transact(() => {
    updateYFragment(ydoc, ydoc.getXmlFragment(field), node, { mapping: new Map(), isOMark: new Map() });
  }, origin);
}

/** Every tab in one JSON doc (downloads, word counts): later tabs start with their name as a heading. */
export function allTabsJSON(ydoc) {
  const list = ydoc.getArray('tabs').toArray().filter(t => t && t.id);
  if (list.length < 2) return toJSON(ydoc);
  const content = [];
  for (const t of list) {
    const j = toJSON(ydoc, t.id === 'default' ? FIELD : `tab-${t.id}`);
    if (t.id !== 'default') content.push({ type: 'heading', attrs: { level: 1 }, content: [{ type: 'text', text: t.title || 'Tab' }] });
    content.push(...(j.content || []));
  }
  return normalize({ type: 'doc', content });
}

/** A fresh Yjs doc holding this JSON (for new docs, copies, imports). */
export function ydocFrom(json, title = '') {
  const ydoc = prosemirrorJSONToYDoc(docSchema(), normalize(json), FIELD);
  if (title) ydoc.getMap('meta').set('title', title);
  return ydoc;
}

export const titleOf = ydoc => String(ydoc.getMap('meta').get('title') || '');

/** What the doc list and search need to know about a doc. */
export function summary(json) {
  const text = textOf(json);
  return {
    snippet: snippetOf(json),
    words: wordCount(text),
    text: text.slice(0, 60_000),
    headings: headingsOf(json).slice(0, 200),
  };
}

export { Y };
